import { isWindows } from "react-device-detect"
import * as devices from "@digits-shared/helpers/devicesHelper"
import colors from "@digits-shared/themes/colors"
import { createGlobalStyle } from "styled-components"

function viewportDimensions() {
  // Sometimes, iOS doesn’t apply safe area insets until after scroll events.
  // Trigger layout shift to apply safe-area-inset
  setTimeout(() => {
    window.scrollTo(0, 1)
    window.scrollTo(0, 0)
  }, 100)

  const onResize = () => {
    const doc = document.documentElement
    // Handle iOS standalone mode
    const isStandalone = "standalone" in navigator && !!navigator.standalone
    const viewportHeight =
      isStandalone && devices.isMobile ? window.screen.height : window.innerHeight
    doc.style.setProperty("--viewport-height", `${viewportHeight}px`)
  }
  window.addEventListener("resize", onResize)
  window.visualViewport?.addEventListener("resize", onResize)
  onResize()

  return undefined
}

function addDeviceClasses() {
  const { isDigitsMacApp, isFirefox, isMacOs, isMobile, isSafari, isTablet, isDesktop } = devices
  const doc = document.documentElement
  if (isDesktop) doc.classList.add("desktop")
  if (isMobile) doc.classList.add("mobile")
  if (isTablet) doc.classList.add("tablet")
  if (isMobile && !isTablet) {
    // shortcut for `.mobile:not(.tablet)`
    doc.classList.add("mobile-only")
  }
  if ("standalone" in navigator && navigator.standalone) {
    doc.classList.add("standalone")
  }

  if (isSafari) doc.classList.add("safari")
  if (isFirefox) doc.classList.add("firefox")
  if (isMacOs) doc.classList.add("macos")
  if (isWindows) doc.classList.add("win")
  if (isDigitsMacApp) doc.classList.add("digits-mac")
}
addDeviceClasses()

export const GlobalStyles = createGlobalStyle`
  :root {
    color-scheme: light;
    --viewport-height: -webkit-fill-available;
    --viewport-width: -webkit-fill-available;
    --outline-color: ${colors.accentBlue}; 
    --outline: var(--outline-color) auto 1px;
    
    *:focus-visible {
      outline-color: var(--outline-color);
    }
  }
  ${viewportDimensions};

  :root.desktop {
    &,
    body,
    main {
        margin: 0;
        padding: 0;
        width: 100vw;
        height: 100vh;
        min-height: 100vh;
        min-width: 100vw;
        overscroll-behavior-x: none;
        overscroll-behavior-y: none;
    }
  }

  :root.mobile {
    &,
    body,
    main {
        width: 100vw;
        min-width: 100vw;
        margin: 0;
        padding: 0;
        position: relative;
        overflow: hidden;
    }

    body {
        height: var(--viewport-height);
    }

    main {
        height: var(--viewport-height);
        width: var(--viewport-width);
        overflow-y: auto;
    }
  }

  :root.mobile-only  {
    main {
        padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom)
        env(safe-area-inset-left);
    }

    &.standalone {
        main {
            padding-bottom: 0;
        }
    }
  }

  :root.tablet {
    @media only screen and (orientation: landscape) {
        main {
            padding: env(safe-area-inset-top) 25% env(safe-area-inset-bottom) 25%;
        }
    }

    @media only screen and (orientation: portrait) {
        main {
            padding: env(safe-area-inset-top) 15% env(safe-area-inset-bottom) 15%;
        }
    }
  }

  * {
    box-sizing: border-box;
  }

  body, b, button, input, select {
    font-family: Avenir, Helvetica, Arial, sans-serif;
    font-weight: 400;
    color: inherit;
  }

  input {
    border-radius: 0;
  }

  input:focus {
    outline: 0;
  }

  @-webkit-keyframes autofill {
    0%,100% {
      color: inherit;
      background: inherit;
    }
  }

  input:-webkit-autofill {
    -webkit-animation-delay: 1s; /* Safari support - any positive time runs instantly */
    -webkit-animation-name: autofill;
    -webkit-animation-fill-mode: both;
  }

  p {
    margin-bottom: 1rem;
    margin-top: 0;
  }

  a {
    color: inherit;
    text-decoration: none;
  }

  b {
    font-weight: 700;
  }

  ul, ol {
    margin: 0;
    padding: 0;
  }

  h2,
  h3,
  h4 {
    margin: 0;
    font-weight: normal;
    font-size: unset;
  }
`
